import { z } from 'zod';
import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';

const REASON_MAX = 500;

export type PauseFormData = {
  mode: 'indefinite' | 'until_date';
  until_date: string;
  reason: string;
};

export type ReasonFormData = {
  reason: string;
};

export function getPauseSchema(i18n: I18n) {
  return z
    .object({
      mode: z.enum(['indefinite', 'until_date']),
      until_date: z.string(),
      reason: z
        .string()
        .trim()
        .min(1, i18n._(msg({ id: 'dunning.action.reason.required', message: 'Reason is required.' })))
        .max(REASON_MAX, i18n._(msg({ id: 'dunning.action.reason.too-long', message: 'Reason must be 500 characters or fewer.' }))),
    })
    .superRefine((data, ctx) => {
      if (data.mode === 'until_date') {
        if (!data.until_date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['until_date'],
            message: i18n._(msg({ id: 'dunning.pause.date.required', message: 'Until date is required.' })),
          });
          return;
        }
        const dt = parseDateInputAsLocalEndOfDay(data.until_date);
        if (!dt) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['until_date'],
            message: i18n._(msg({ id: 'dunning.pause.date.invalid', message: 'Invalid date.' })),
          });
          return;
        }
        if (dt.getTime() <= Date.now()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['until_date'],
            message: i18n._(msg({ id: 'dunning.pause.date.future', message: 'Date must be in the future.' })),
          });
        }
      }
    });
}

export function getReasonSchema(i18n: I18n) {
  return z.object({
    reason: z
      .string()
      .trim()
      .min(1, i18n._(msg({ id: 'dunning.action.reason.required', message: 'Reason is required.' })))
      .max(REASON_MAX, i18n._(msg({ id: 'dunning.action.reason.too-long', message: 'Reason must be 500 characters or fewer.' }))),
  });
}

// getOptionalReasonSchema is the optional-reason variant. Used by the
// awaiting_approval cancel modal where the operator MAY but is not REQUIRED
// to explain the cancellation (per backend OptionalReasonRequest DTO).
export function getOptionalReasonSchema(i18n: I18n) {
  return z.object({
    reason: z
      .string()
      .trim()
      .max(REASON_MAX, i18n._(msg({ id: 'dunning.action.reason.too-long', message: 'Reason must be 500 characters or fewer.' }))),
  });
}

// parseDateInputAsLocalEndOfDay reads a `yyyy-MM-dd` day string (the
// `formatLocalDate` output PauseModal stores) as the LOCAL end-of-day
// (23:59:59.999). new Date('2026-05-15') parses as UTC midnight, which is
// "yesterday at 4pm" in TZ=America/Los_Angeles — that
// breaks the future-date check for users east of UTC who pick "today" and
// makes the pause expire a day earlier than intended for users west of UTC.
export function parseDateInputAsLocalEndOfDay(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!year || !month || !day) return null;
  const dt = new Date(year, month - 1, day, 23, 59, 59, 999);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}
